import type { BloqueioAtivo } from '@eva/schemas';
/**
 * Cliente do chat (`/api/chat/*`), compartilhado por todos os portais.
 *
 * Os endpoints não usam o envelope `{ data }`; as respostas vêm cruas.
 * Quem fala com quem é decidido no servidor (canais e `politica-de-chat.service`):
 * aqui não há decisão de autorização, só transporte e forma.
 */
import { api } from '../api/client';

export type MediaType = 'image' | 'video' | 'audio' | 'file';

export type MessageContent =
  | { type: 'text'; text: string }
  | {
      type: 'media';
      mediaType: MediaType;
      url: string;
      filename?: string;
      size?: number;
      mimeType?: string;
      width?: number;
      height?: number;
      transcodingStatus?: 'PENDING' | 'TRANSCODING' | 'READY' | 'FAILED';
      transcodingJobId?: string;
      transcodedUrls?: Record<string, string>;
    }
  | { type: 'system'; code: string; payload?: Record<string, unknown> };

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  seq?: number;
  content: MessageContent;
  createdAt: string;
  readBy?: string[];
  internal?: boolean;
  author?: { id: string; type: string; displayName: string; portraitUrl?: string | null };
  /** Só no cliente: enviada e ainda sem confirmação, ou falhou. */
  pending?: boolean;
  failed?: boolean;
}

export interface ConversationParticipant {
  id: string;
  fullName?: string | null;
  avatarUrl?: string | null;
  role?: string;
  isMe?: boolean;
}

export interface Conversation {
  id: string;
  type: 'direct' | 'group';
  name?: string;
  participants: ConversationParticipant[];
  participantIds?: string[];
  lastMessage?: ChatMessage | null;
  seq?: number;
  participantState?: Record<string, { lastReadSeq: number; lastReadAt?: string }>;
  computedUnread?: number;
  createdAt: string;
  updatedAt: string;
  /** Conversa com um canal (A144): a paciente fala com o canal, não com uma pessoa. */
  canalId?: string;
  pacienteId?: string;
  canal?: ResumoDoCanal;
}

/** O canal de uma conversa, como a API o resume (A144). */
export interface ResumoDoCanal {
  id: string;
  nome: string;
  icone: string;
  /** A foto do canal (A149); sem ela, o ícone. */
  fotoUrl?: string | null;
  mostrarNomeDeQuemResponde: boolean;
}

/** Um canal que a paciente pode procurar (A144). */
export interface CanalDoChat {
  id: string;
  nome: string;
  descricao: string | null;
  icone: string;
  /** A foto do canal (A149); sem ela, o ícone. */
  fotoUrl?: string | null;
  /** A conversa dela com o canal, se já existe. */
  conversaId: string | null;
  /** Travado para ela pela marca (bloqueios, 14/10/2026): aparece com cadeado e o toque mostra o aviso. */
  bloqueio?: BloqueioAtivo | null;
}

export interface MessagesPage {
  items: ChatMessage[];
  latestSeq: number;
  conversationSeq: number;
  hasMore: boolean;
}

/** Quem a usuária pode chamar para uma conversa nova (`/chat/eligible-users`). */
export interface ChatEligibleUser {
  id: string;
  name: string;
  avatarUrl?: string | null;
  role?: string;
  /** Profissional da rede Eva: o servidor usa este id como participante. */
  medicalProviderId?: string | null;
  partnerId?: string | null;
}

export const chatApi = {
  changes(sinceVersion?: number): Promise<{ changed: boolean; version: number }> {
    const qs = sinceVersion != null ? `?sinceVersion=${sinceVersion}` : '';
    return api.get(`/api/chat/changes${qs}`);
  },

  listConversations(): Promise<{ items: Conversation[]; version?: number }> {
    return api.get('/api/chat/conversations', { params: { limit: 50 } });
  },

  messages(conversationId: string, params: { sinceSeq?: number; limit?: number } = {}): Promise<MessagesPage> {
    return api.get(`/api/chat/conversations/${conversationId}/messages`, { params: { limit: 50, ...params } });
  },

  send(conversationId: string, content: MessageContent, opts: { internal?: boolean } = {}): Promise<ChatMessage> {
    return api.post(`/api/chat/conversations/${conversationId}/messages`, { content, internal: opts.internal });
  },

  /** Os canais da marca que a paciente pode procurar (A144). */
  async canais(): Promise<CanalDoChat[]> {
    const r = await api.get<{ items?: CanalDoChat[] }>('/api/chat/canais');
    return r?.items ?? [];
  },

  /** A paciente escreve para um canal: abre (ou reaproveita) a conversa dela com ele (A144). */
  escreverAoCanal(canalId: string, content: MessageContent, clientMessageId?: string): Promise<{ conversationId: string; message: ChatMessage }> {
    return api.post(`/api/chat/canais/${encodeURIComponent(canalId)}/mensagens`, { content, clientMessageId });
  },

  /** Os canais pelos quais quem atende fala com esta pessoa (A144, fase 2). Vazio: conversa direta. */
  async canaisDaEquipe(pessoaId: string): Promise<CanalDoChat[]> {
    const r = await api.get<{ items?: CanalDoChat[] }>(`/api/chat/canais/equipe/${encodeURIComponent(pessoaId)}`);
    return r?.items ?? [];
  },

  /**
   * Primeira mensagem para alguém: cria a conversa se não existir. Quem atende
   * um canal para a pessoa fala pelo canal (A144); `canalId` escolhe qual.
   */
  directSend(toUserId: string, content: MessageContent, clientMessageId?: string, canalId?: string | null): Promise<{ conversationId: string; message: ChatMessage }> {
    return api.post('/api/chat/direct/send', { toUserId, content, clientMessageId, ...(canalId ? { canalId } : {}) });
  },

  markRead(conversationId: string): Promise<void> {
    return api.post(`/api/chat/conversations/${conversationId}/read`, {}).then(() => undefined);
  },

  async upload(conversationId: string, file: File): Promise<{ url: string; filename: string; size: number; mimeType: string; status?: string; jobId?: string }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post(`/api/chat/conversations/${conversationId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 5 * 60 * 1000,
    });
  },

  signMedia(path: string): Promise<{ url: string }> {
    return api.get('/api/chat/media/sign', { params: { path } });
  },

  /**
   * Contatos elegíveis para conversa nova. A política (quem fala com quem)
   * é do servidor; `q` filtra por nome/e-mail sem acento.
   */
  async eligibleUsers(q?: string): Promise<ChatEligibleUser[]> {
    const r = await api.get<{ items?: Array<Record<string, unknown>> }>('/api/chat/eligible-users', { params: q?.trim() ? { q: q.trim() } : undefined });
    return (r?.items ?? []).map((p) => ({
      id: String(p.id),
      name: String(p.name || p.fullName || p.displayName || (typeof p.email === 'string' ? p.email.split('@')[0] : '') || 'Usuário'),
      avatarUrl: (p.avatarUrl as string | null | undefined) ?? (p.photoUrl as string | null | undefined) ?? null,
      role: p.role as string | undefined,
      medicalProviderId: (p.medicalProviderId as string | null | undefined) ?? null,
      partnerId: (p.partnerId as string | null | undefined) ?? null,
    }));
  },
};

/** Na conversa de canal, quem vê é a paciente? (A144) */
export function ehPacienteDoCanal(c: Conversation, meId?: string | null): boolean {
  if (!c.canalId) return false;
  if (meId && c.pacienteId) return c.pacienteId === meId;
  return (c.participants ?? []).some((p) => p.role === 'CANAL');
}

/**
 * Título de uma conversa para quem a vê: nome do grupo, ou o outro lado. Na
 * conversa de canal (A144), a paciente vê o canal; quem atende vê a paciente.
 */
export function conversationTitle(c: Conversation, meId?: string | null): string {
  if (c.canalId) {
    if (ehPacienteDoCanal(c, meId)) return c.canal?.nome || c.name || 'Atendimento';
    const paciente = (c.participants ?? []).find((p) => p.id === c.pacienteId);
    return paciente?.fullName || 'Usuária(o)';
  }
  if (c.type === 'group' && c.name) return c.name;
  const outros = (c.participants ?? []).filter((p) => !p.isMe && p.id !== meId);
  if (outros.length === 0) return c.name || 'Conversa';
  if (outros.length === 1) return outros[0].fullName || 'Usuário';
  const doisPrimeiros = outros.slice(0, 2).map((o) => o.fullName || 'Usuário').join(', ');
  return outros.length > 2 ? `${doisPrimeiros} +${outros.length - 2}` : doisPrimeiros;
}

export function conversationCounterpart(c: Conversation, meId?: string | null): ConversationParticipant | null {
  // Para quem atende um canal, o outro lado é a paciente, não um colega.
  if (c.canalId && !ehPacienteDoCanal(c, meId)) return (c.participants ?? []).find((p) => p.id === c.pacienteId) ?? null;
  const outros = (c.participants ?? []).filter((p) => !p.isMe && p.id !== meId);
  return outros[0] ?? null;
}

/** Prévia em uma linha da última mensagem. */
export function messagePreview(m?: ChatMessage | null): string {
  if (!m) return '';
  const c = m.content;
  if (!c) return '';
  if (c.type === 'text') return c.text;
  if (c.type === 'media') return c.mediaType === 'image' ? 'Imagem' : c.mediaType === 'video' ? 'Vídeo' : c.mediaType === 'audio' ? 'Áudio' : 'Arquivo';
  return '';
}

export function unreadCount(c: Conversation, meId?: string | null): number {
  if (typeof c.computedUnread === 'number') return c.computedUnread;
  if (meId && c.seq != null && c.participantState?.[meId]) {
    return Math.max(0, (c.seq ?? 0) - (c.participantState[meId].lastReadSeq ?? 0));
  }
  return 0;
}
